import { Grid } from '@material-ui/core'
import QueryString from 'querystring'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { set_la_api_token } from '../../helpers/cookies'
import { qps_from_search } from '../../helpers/misc'
import routes from '../../routes'
import { dataLayer } from '../../store/analytics.middleware'
import { Box, Text } from '../Shared/Base'
import { FormElementGlobalError } from '../Shared/Form/Element'
import FormField, { FocusableField, useFormState } from '../Shared/Form/Field'
import { FormPasswordInput, FormTextFieldInput } from '../Shared/Form/Input'
import { valid_email, valid_required } from '../Shared/Form/validators'
import { useCheckAuthentication, useFetchApi, useSignRedirect } from '../Shared/hooks'
import LinkSafe from '../Shared/LinkSafe'
import LoadingBar from '../Shared/LoadingBar'

const form_validators = {
  password: { required: valid_required, length: v => v.length > 5 },
  email: { required: valid_required, email: valid_email },
  cgu_check: { required: valid_required }
}

const RightStyled = styled(Box)`
  background: url('https://res.cloudinary.com/loueragile/image/upload/f_auto/v1/web/photos/signup.jpg');
  background-size: cover;
  background-repeat: no-repeat;
`

const SignUp = () => {
  const fetcher = useFetchApi()
  const check_authentication = useCheckAuthentication()
  const sign_redirect = useSignRedirect()

  const onSubmitSuccess = React.useCallback(
    async values => {
      const { access_token, id } = await fetcher('user/', {
        method: 'POST',
        body: JSON.stringify({ email: values.email, password: values.password })
      })
      set_la_api_token(access_token)
      dataLayer.push({
        event: 'eventGA',
        category: 'User',
        action: 'Create',
        label: id
      })
      await check_authentication()
      sign_redirect()
    },
    [check_authentication, fetcher, sign_redirect]
  )

  const location_search = useLocation().search
  const search_params = React.useMemo(() => qps_from_search(location_search), [location_search])
  const initial_values = React.useMemo(() => {
    return {
      email: '',
      password: '',
      cgu_check: false
    }
  }, [])

  const form = useFormState({
    initial_values,
    validators: form_validators,
    onSubmitSuccess
  })

  if (true) {
    return null
  }

  return (
    <Box display="flex" width="100%" minHeight="100vh">
      <Box width={{ md: '65%', xs: '100%' }} m="auto">
        <Box maxWidth={450} m="auto" px={20} py={20}>
          <Box width="100%" textAlign="center">
            <Box as={LinkSafe} to={routes.homepage.route.reverse()} display="block" mb={30}>
              <Box
                as="img"
                src="https://res.cloudinary.com/loueragile/image/upload/v1653311807/web/jinka/Logo-Jinka-a.svg"
                width={129}
                alt="Logo - Jinka"
                position={{ md: 'absolute', xs: 'relative' }}
                top={{ md: 50, xs: 'auto' }}
                left={{ md: 50, xs: 'auto' }}
                ml={{ md: 0, xs: 'auto' }}
              />
            </Box>
            <Box
              as="p"
              className="h2-like"
              mb={50}
              pb={10}
              px={20}
              borderColor="n_tertiary"
              borderBottom="1px solid"
              textAlign="center"
              display="inline-block"
            >
              Inscription
            </Box>
          </Box>
          <Box as="form" onSubmit={form.onSubmit} onChange={form.onChange} width="100%">
            <FormElementGlobalError globalError={form.globalError} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FocusableField
                  showRequiredError={form.submittedOnce}
                  label="E-mail"
                  name="email"
                  type="email"
                  placeholder="Votre e-mail"
                  value={form.values.email}
                  validators={form.validators.email}
                  errorLabels={{
                    required: "L'e-mail est obligatoire",
                    email: "L'e-mail est incorrect"
                  }}
                >
                  {FormTextFieldInput}
                </FocusableField>
              </Grid>
              <Grid item xs={12}>
                <FocusableField
                  label="Mot de passe"
                  showRequiredError={form.submittedOnce}
                  name="password"
                  placeholder="6 caractères"
                  value={form.values.password}
                  validators={form.validators.password}
                  errorLabels={{
                    required: 'Mot de passe obligatoire',
                    length: 'Au moins 6 caractères'
                  }}
                >
                  {FormPasswordInput}
                </FocusableField>
              </Grid>
            </Grid>

            <Box as="label" className="form-checkbox" mt={20}>
              J’accepte la politique de confidentialité et les{' '}
              <Box
                as="a"
                href={routes.cgu.route.reverse()}
                target="_blank"
                color="n_tertiary"
                className="tdu"
                rel="noopener noreferrer"
              >
                conditions générales d’utilisation
              </Box>
              .
              <input
                type="checkbox"
                id="cgu_check"
                onChange={() => {
                  form.onChangeByNameValue('cgu_check', !form.values.cgu_check)
                }}
                checked={form.values.cgu_check}
              />
              <span className="checkmark" />
            </Box>

            <FormField
              value={form.values.cgu_check}
              validators={form.validators.cgu_check}
              showRequiredError={form.submittedOnce}
            >
              {({ error }) =>
                Boolean(error) && (
                  <Box
                    as="p"
                    className="fz-16"
                    backgroundColor="bg_negative"
                    color="negative"
                    px={20}
                    py={10}
                    borderRadius={5}
                    mt={20}
                  >
                    Notre service nécessite le traitement de données personnelles (informations de
                    contact). Pour y souscrire vous devez accepter notre politique de
                    confidentialité et nos conditions générales d’utilisation.
                  </Box>
                )
              }
            </FormField>

            <Box
              as="button"
              className="btn is-n_secondary is-medium"
              mt={30}
              width="100%"
              disabled={form.loading}
            >
              S'inscrire
            </Box>

            <Box mt={20} textAlign="center">
              Déjà un compte ?{' '}
              <Link
                to={{
                  pathname: routes.sign.route.reverse({ action: 'in' }),
                  search: QueryString.stringify(search_params)
                }}
              >
                <Text color="n_tertiary" as="span" className="tdh">
                  Se connecter
                </Text>
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      <RightStyled
        width="35%"
        display={{ xs: 'none', md: 'flex' }}
        textAlign="center"
        color="white"
        px={20}
        py={20}
      />
    </Box>
  )
}

const form_from_alert_validators = {
  password: { required: valid_required, length: v => v.length > 5 }
}
const form_from_alert_initial_values = {
  password: ''
}
const SignupFromAlert = ({ alert_email, alert_id }) => {
  const fetcher = useFetchApi()
  const sign_redirect = useSignRedirect()
  const check_authentication = useCheckAuthentication()

  const onSubmitSuccess = React.useCallback(
    async ({ password }) => {
      const { access_token, id } = await fetcher(`user/create_from_alert/${alert_id}`, {
        method: 'POST',
        body: JSON.stringify({
          password
        })
      })
      dataLayer.push({
        event: 'eventGA',
        category: 'User',
        action: 'Create',
        label: id
      })
      set_la_api_token(access_token)
      await check_authentication()
      sign_redirect()
    },
    [alert_id, check_authentication, fetcher, sign_redirect]
  )

  const form = useFormState({
    initial_values: form_from_alert_initial_values,
    validators: form_from_alert_validators,
    onSubmitSuccess
  })
  return (
    <Box display="flex" width="100%" minHeight="100vh">
      <Box width={{ md: '65%', xs: '100%' }} m="auto">
        <Box maxWidth={450} m="auto" px={20} py={20}>
          <Box width="100%" textAlign="center">
            <Box as={LinkSafe} to={routes.homepage.route.reverse()} display="block" mb={30}>
              <Box
                as="img"
                src="https://res.cloudinary.com/loueragile/image/upload/v1653311807/web/jinka/Logo-Jinka-a.svg"
                width={129}
                alt="Logo - Jinka"
                position={{ md: 'absolute', xs: 'relative' }}
                top={{ md: 50, xs: 'auto' }}
                left={{ md: 50, xs: 'auto' }}
                ml={{ md: 0, xs: 'auto' }}
              />
            </Box>
            <Box
              as="p"
              className="h2-like"
              mb={50}
              pb={10}
              px={20}
              borderColor="n_tertiary"
              borderBottom="1px solid"
              textAlign="center"
              display="inline-block"
            >
              Inscription
            </Box>
          </Box>
          <Box as="form" onSubmit={form.onSubmit} onChange={form.onChange} width="100%">
            <FormElementGlobalError globalError={form.globalError} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  as="p"
                  className="fz-16"
                  backgroundColor="bg_primary"
                  color="primary"
                  borderRadius={5}
                  px={20}
                  py={10}
                >
                  Pour profiter des nouvelles fonctionnalités de l'alerte, veuillez créer un compte
                  pour accéder à votre tabeau de bord.
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormTextFieldInput value={alert_email} label="E-mail" disabled />
              </Grid>
              <Grid item xs={12}>
                <FocusableField
                  label="Mot de passe"
                  showRequiredError={form.submittedOnce}
                  name="password"
                  placeholder="6 caractères"
                  value={form.values.password}
                  validators={form.validators.password}
                  errorLabels={{
                    length: 'Au moins 6 caractères'
                  }}
                >
                  {FormPasswordInput}
                </FocusableField>
              </Grid>
            </Grid>

            <Box
              as="button"
              className="btn is-n_secondary is-medium"
              mt={40}
              width="100%"
              disabled={form.loading}
            >
              S'inscrire
            </Box>
          </Box>
        </Box>
      </Box>

      <RightStyled
        width="35%"
        display={{ xs: 'none', md: 'flex' }}
        textAlign="center"
        color="white"
        px={20}
        py={20}
      />
    </Box>
  )
}

const SignUpCF = () => {
  const [my_alert, set_my_alert] = React.useState(null)
  const location_search = useLocation().search
  const search_params = React.useMemo(() => qps_from_search(location_search), [location_search])
  const alert_id_migrate_signup = ((search_params.r_pathname || '').match(
    /^\/asrenter\/alert\/dashboard\/([a-zA-Z0-9]*)/
  ) || [])[1]
  const fetcher = useFetchApi()

  React.useEffect(() => {
    const fetch_alert = async () => {
      const result = await fetcher(`alert/${alert_id_migrate_signup}`)
      set_my_alert(result)
    }

    if (alert_id_migrate_signup) {
      fetch_alert()
    }
  }, [alert_id_migrate_signup, fetcher])
  if (alert_id_migrate_signup) {
    if (!my_alert) return <LoadingBar />

    return <SignupFromAlert alert_email={my_alert.email} alert_id={my_alert.id} />
  } else {
    return <SignUp />
  }
}

export default SignUpCF
